/**
 * 入力画面の登録タブの一覧（Issue #467）。components/ocg/RegisterPanel の「最新の未確認」の強調をやめ、スマホで押しやすい
 * 1 行 1 件のボタンにした。並びは**起きた順**（初手 1〜5 枚目 → ドロー…）で、既定は未確認だけ。
 * 行を押すと選び直し（onPick。ドローはアリーナの横長のデッキ一覧が開く）、未確認の行の「OK」は今の値で確定（onConfirm）。
 * 記録済みの操作と合わなくなる変更は、force のチェックボックスを置かず、ページのお知らせのやり直しボタン（force_required）で書く。
 */
import { useState } from 'react';
import { playerColor, usePlayerColors } from './common';
import { btn } from './RespondTab';

/** 起きた順（buildRegisterItems は新しい順なので逆にする）。同じ時点（両者の初手）は P1 → P2 */
export const chronological = (items) => [...items].sort((a, b) => a.msg_index - b.msg_index || (a.player ?? a.team ?? 9) - (b.player ?? b.team ?? 9) || a.seq - b.seq);

/** 同じプレイヤーの、item より後の最初の未確認のドロー（選んだら次へ進む）。無ければ null */
export function nextUnconfirmedDraw(items, item) {
  if (!item || item.type !== 'draw') return null;
  return chronological(items).find((i) => i.type === 'draw' && !i.confirmed && i.team === item.team
    && (i.msg_index > item.msg_index || (i.msg_index === item.msg_index && i.seq > item.seq))) ?? null;
}

/** 項目の今の値の文（サーバーが付ける value_text。Issue #665） */
export const valueText = (item) => item?.value_text ?? '';

export default function RegisterList({ items = [], names = {}, thumbs = {}, players = [], busy = false, onPick, onConfirm }) {
  const colors = usePlayerColors();
  const unconfirmed = items.filter((i) => !i.confirmed).length;
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(true);
  const shown = chronological(items).filter((i) => !onlyUnconfirmed || !i.confirmed);
  const name = (p) => players?.[p]?.name ?? `P${p + 1}`;
  const chip = (on) => ({ ...btn(on ? 'primary' : 'plain'), minHeight: 32, padding: '4px 12px', fontSize: 13 });
  return (
    <div data-testid="register-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', scrollSnapAlign: 'start' }}>
        <button type="button" aria-pressed={onlyUnconfirmed} style={chip(onlyUnconfirmed)} onClick={() => setOnlyUnconfirmed(true)}>未確認 {unconfirmed}</button>
        <button type="button" aria-pressed={!onlyUnconfirmed} style={chip(!onlyUnconfirmed)} onClick={() => setOnlyUnconfirmed(false)}>全部 {items.length}</button>
      </div>
      {shown.map((item) => {
        const color = playerColor(item.player, colors) ?? '#6a7690';
        const image = item.type === 'draw' ? thumbs[String(item.code)] : null;
        return (
          <div key={item.key} data-testid="register-row" data-register-key={item.key} data-unconfirmed={item.confirmed ? undefined : '1'}
            style={{ display: 'flex', alignItems: 'stretch', gap: 6, borderLeft: `4px solid ${color}`, background: '#121a2e', borderRadius: 8, overflow: 'hidden', scrollSnapAlign: 'start' }}>
            <button type="button" data-testid="register-row-pick" disabled={busy} onClick={() => onPick?.(item)}
              style={{ flex: 1, minWidth: 0, minHeight: 52, display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: 'none', border: 'none', color: '#e8eefc', textAlign: 'left', cursor: 'pointer' }}>
              {item.type === 'draw' ? (image ? <img alt="" src={image} width={30} height={44} style={{ objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                : <span style={{ width: 30, height: 44, borderRadius: 3, background: '#2a2f45', flexShrink: 0 }} />) : null}
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 11, opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.player === 0 || item.player === 1 ? `${name(item.player)} ` : ''}{item.label}{item.confirmed ? ' ・確認済み' : ''}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{valueText(item)}</span>
              </span>
            </button>
            {!item.confirmed ? (
              <button type="button" data-testid="register-row-ok" disabled={busy} onClick={() => onConfirm?.(item)}
                style={{ ...btn('plain'), minWidth: 56, borderRadius: 0, border: 'none', borderLeft: '1px solid #2a3552' }}>OK</button>
            ) : null}
          </div>
        );
      })}
      {!shown.length ? <div style={{ fontSize: 13, opacity: 0.7 }}>{onlyUnconfirmed ? '未確認はありません' : 'まだありません'}</div> : null}
    </div>
  );
}
